import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  X,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { CURRENT_TUTORIAL_VERSION, STEPS, stepsForUser } from "@/components/tutorial/tutorialSteps";
import SpotlightOverlay from "@/components/tutorial/SpotlightOverlay";

const CARD_W = 380;   // approx panel width — keep in sync with tailwind class
const CARD_MARGIN = 16;

/**
 * TutorialTour — auto-launched guided walkthrough.
 *
 * - Spotlights the actual DOM element being described (SVG mask + glowing outline)
 * - Positions the info card intelligently near the target (falls back to bottom-right)
 * - Renders inline visual "examples" for concept-heavy steps
 * - Supports two modes:
 *     • "full"       first-time onboarding → every applicable step
 *     • "whats-new"  only steps introduced after user.tutorial_version →
 *                    the "look at this new feature" mini-tour we auto-launch
 *                    for existing users after shipping.
 */
export default function TutorialTour({ open, onClose, forceFull = false }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const cardRef = useRef(null);

  // Determine what to show for this user. `forceFull` (Replay Tutorial button)
  // overrides the version check so up-to-date users can still watch the tour.
  const { steps, mode } = useMemo(() => {
    if (forceFull) {
      const isAdmin = user?.role === "admin";
      return {
        steps: STEPS.filter((s) => !s.adminOnly || isAdmin),
        mode: "full",
      };
    }
    return stepsForUser(user);
  }, [user, forceFull]);
  const step = steps[i];
  const total = steps.length;
  const isWhatsNew = mode === "whats-new";

  // Reset step index whenever the tour opens (replay support)
  useEffect(() => {
    if (open) setI(0);
  }, [open]);

  // Auto-navigate to the step's route + measure target rect
  useEffect(() => {
    if (!open || !step) return;
    if (step.route) nav(step.route);

    // Give the route time to render, then measure the target
    let cancelled = false;
    let preRan = false;

    const measure = (attempt = 0) => {
      if (cancelled) return;
      if (!step.target) {
        setTargetRect(null);
        return;
      }
      // Try to run preAction once the route root is likely mounted.
      // preAction should return `true` when it actually did its work
      // (e.g. the tab it wanted to click was in the DOM). We retry until
      // it returns truthy, so the tour handles lazy renders / route
      // transitions gracefully.
      if (!preRan && typeof step.preAction === "function") {
        try {
          const done = step.preAction();
          if (done !== false) preRan = true;
        } catch {
          /* noop — retry next tick */
        }
      }
      const el = document.querySelector(step.target);
      if (!el) {
        if (attempt < 40) setTimeout(() => measure(attempt + 1), 80);
        return;
      }
      // Scroll into view first so the rect lands correctly
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } catch {
        /* noop */
      }
      // Measure after settle
      setTimeout(() => {
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        setTargetRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      }, 350);
    };
    measure();

    // Keep rect fresh on scroll / resize
    const onWinChange = () => {
      if (!step.target) return;
      const el = document.querySelector(step.target);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setTargetRect({ x: r.left, y: r.top, w: r.width, h: r.height });
    };
    window.addEventListener("scroll", onWinChange, { passive: true, capture: true });
    window.addEventListener("resize", onWinChange);
    return () => {
      cancelled = true;
      window.removeEventListener("scroll", onWinChange, true);
      window.removeEventListener("resize", onWinChange);
    };
  }, [open, step, nav]);

  const complete = useCallback(async () => {
    try {
      await api.post("/auth/tutorial/complete", { version: CURRENT_TUTORIAL_VERSION });
      await refresh?.();
    } catch {
      /* silent — retry on next login */
    }
    onClose?.();
  }, [onClose, refresh]);

  const skip = () => complete();
  const next = () => {
    if (i >= total - 1) return complete();
    setI((v) => v + 1);
  };
  const prev = () => setI((v) => Math.max(0, v - 1));

  // Guard: nothing to render (empty tour or not open)
  if (!open || !step) return null;

  const Icon = step.icon || Sparkles;
  const isAdmin = user?.role === "admin";
  const showAdminBullets = isAdmin && Array.isArray(step.adminBullets);

  // Position the card near the target rect. Falls back to bottom-right.
  const cardStyle = computeCardStyle(step, targetRect);

  return (
    <>
      <SpotlightOverlay target={step.target} />

      <div
        ref={cardRef}
        className="fixed z-50 w-[calc(100vw-32px)] sm:w-[380px] rounded-xl border border-border/70 bg-white shadow-2xl shadow-black/25 animate-fade-in"
        data-testid="tutorial-tour"
        role="dialog"
        aria-labelledby="tutorial-title"
        style={cardStyle}
      >
        {/* Progress bar */}
        <div className="h-1 bg-secondary rounded-t-xl overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((i + 1) / total) * 100}%` }}
            data-testid="tutorial-progress"
          />
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                <Icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground flex items-center gap-2">
                  {isWhatsNew ? (
                    <span className="text-primary font-semibold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> What's new
                    </span>
                  ) : (
                    <>Step {i + 1} of {total}</>
                  )}
                  {step.isNew && !isWhatsNew && (
                    <span className="px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary text-[9px] font-bold tracking-wider">
                      NEW
                    </span>
                  )}
                </div>
                <h3
                  id="tutorial-title"
                  className="font-heading font-bold text-lg leading-tight truncate"
                  data-testid="tutorial-title"
                >
                  {step.title}
                </h3>
              </div>
            </div>
            <button
              type="button"
              onClick={skip}
              className="p-1 -m-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
              aria-label="Skip tutorial"
              data-testid="tutorial-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {step.intro && (
            <p className="text-sm text-muted-foreground mb-3">{step.intro}</p>
          )}

          {step.example && (
            <div className="mb-3" data-testid="tutorial-example">
              {step.example}
            </div>
          )}

          <ul className="space-y-2 text-sm" data-testid="tutorial-bullets">
            {step.bullets?.map((b, idx) => (
              <li key={idx} className="flex gap-2">
                <span className="text-primary mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-primary" />
                <span className="text-foreground/90">{b}</span>
              </li>
            ))}
            {showAdminBullets &&
              step.adminBullets.map((b, idx) => (
                <li key={`a-${idx}`} className="flex gap-2">
                  <span className="text-[#7c5a14] mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[#E5A93D]" />
                  <span className="text-foreground/90">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#7c5a14] mr-1.5">
                      Admin
                    </span>
                    {b}
                  </span>
                </li>
              ))}
          </ul>

          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border/60">
            <button
              type="button"
              onClick={skip}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              data-testid="tutorial-skip"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={prev}
                disabled={i === 0}
                data-testid="tutorial-prev"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={next}
                data-testid="tutorial-next"
              >
                {i === total - 1 ? (
                  <>
                    Finish <CheckCircle2 className="w-3.5 h-3.5 ml-1" />
                  </>
                ) : (
                  <>
                    Next <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Choose card position:
 *  1. If no target rect → bottom-right of the viewport
 *  2. Try the requested placement first
 *  3. Fall back to whichever side has the most room
 *  Card is clamped to viewport with a small margin.
 */
function computeCardStyle(step, rect) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardW = Math.min(CARD_W, vw - CARD_MARGIN * 2);
  const est = { w: cardW, h: 360 }; // rough card height estimate for placement

  // Concept steps (no target) → bottom-right anchored card
  if (!rect || step.placement === "center" || !step.target) {
    return {
      right: `${CARD_MARGIN}px`,
      bottom: `${CARD_MARGIN}px`,
      width: `${cardW}px`,
    };
  }

  const placements = [step.placement, "bottom", "right", "top", "left"];
  const seen = new Set();

  for (const p of placements) {
    if (!p || seen.has(p)) continue;
    seen.add(p);
    const pos = tryPlacement(p, rect, est, vw, vh);
    if (pos) return { ...pos, width: `${cardW}px` };
  }

  // Nothing fit — dock to bottom-right
  return {
    right: `${CARD_MARGIN}px`,
    bottom: `${CARD_MARGIN}px`,
    width: `${cardW}px`,
  };
}

function tryPlacement(placement, rect, est, vw, vh) {
  const gap = 20;
  if (placement === "bottom") {
    const top = rect.y + rect.h + gap;
    if (top + est.h < vh - CARD_MARGIN) {
      const left = clamp(rect.x + rect.w / 2 - est.w / 2, CARD_MARGIN, vw - est.w - CARD_MARGIN);
      return { top: `${top}px`, left: `${left}px` };
    }
  }
  if (placement === "top") {
    const top = rect.y - est.h - gap;
    if (top > CARD_MARGIN) {
      const left = clamp(rect.x + rect.w / 2 - est.w / 2, CARD_MARGIN, vw - est.w - CARD_MARGIN);
      return { top: `${top}px`, left: `${left}px` };
    }
  }
  if (placement === "right") {
    const left = rect.x + rect.w + gap;
    if (left + est.w < vw - CARD_MARGIN) {
      const top = clamp(rect.y + rect.h / 2 - est.h / 2, CARD_MARGIN, vh - est.h - CARD_MARGIN);
      return { top: `${top}px`, left: `${left}px` };
    }
  }
  if (placement === "left") {
    const left = rect.x - est.w - gap;
    if (left > CARD_MARGIN) {
      const top = clamp(rect.y + rect.h / 2 - est.h / 2, CARD_MARGIN, vh - est.h - CARD_MARGIN);
      return { top: `${top}px`, left: `${left}px` };
    }
  }
  return null;
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}
